import { create } from 'zustand';
import type { FloorPlanWall, FloorPlanRect, FloorPlanTool, FloorPlanData, FloorPlanElement } from '@/types/floor-plan';
import { getFloorPlan, updateFloorPlan } from '@/api/floor-plan';

interface DrawingState {
  startX: number;
  startY: number;
  currentX: number;
  currentY: number;
}

interface FloorPlanStore {
  // Data
  walls: FloorPlanWall[];
  rects: FloorPlanRect[];

  // Edit copies
  editWalls: FloorPlanWall[];
  editRects: FloorPlanRect[];

  // UI state
  activeTool: FloorPlanTool;
  selectedElement: FloorPlanElement | null;
  isDrawing: boolean;
  drawingState: DrawingState | null;
  isLoading: boolean;
  generation: number;
  reset: () => void;

  // Actions
  setActiveTool: (tool: FloorPlanTool) => void;
  setSelectedElement: (element: FloorPlanElement | null) => void;

  // Drawing
  startDrawing: (x: number, y: number) => void;
  updateDrawing: (x: number, y: number) => void;
  finishDrawing: (shiftKey: boolean) => void;
  cancelDrawing: () => void;

  // Element operations
  addWall: (wall: FloorPlanWall) => void;
  addRect: (rect: FloorPlanRect) => void;
  updateWall: (id: string, updates: Partial<FloorPlanWall>) => void;
  updateRect: (id: string, updates: Partial<FloorPlanRect>) => void;
  deleteElement: (id: string) => void;
  moveElement: (id: string, dx: number, dy: number) => void;

  // Edit mode
  enterEditMode: () => void;
  cancelEditMode: () => void;

  // Persistence
  loadFloorPlan: (floorId: string) => Promise<void>;
  saveFloorPlan: (floorId: string) => Promise<void>;
}

function snapAngle45(x1: number, y1: number, x2: number, y2: number): { x: number; y: number } {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const angle = Math.atan2(dy, dx);
  const snapped = Math.round(angle / (Math.PI / 4)) * (Math.PI / 4);
  const length = Math.sqrt(dx * dx + dy * dy);
  return {
    x: x1 + length * Math.cos(snapped),
    y: y1 + length * Math.sin(snapped),
  };
}

export const useFloorPlanStore = create<FloorPlanStore>((set, get) => ({
  walls: [],
  rects: [],
  editWalls: [],
  editRects: [],
  activeTool: 'select',
  selectedElement: null,
  isDrawing: false,
  drawingState: null,
  isLoading: false,
  generation: 0,
  reset: () => set((s) => ({ walls: [], rects: [], editWalls: [], editRects: [],
    activeTool: 'select', selectedElement: null, isDrawing: false, drawingState: null,
    isLoading: false, generation: s.generation + 1 })),

  setActiveTool: (tool) => set({ activeTool: tool, selectedElement: null }),
  setSelectedElement: (element) => set({ selectedElement: element }),

  startDrawing: (x, y) => {
    const { activeTool } = get();
    if (activeTool === 'select') return;
    set({ isDrawing: true, drawingState: { startX: x, startY: y, currentX: x, currentY: y } });
  },

  updateDrawing: (x, y) => {
    const { isDrawing, drawingState } = get();
    if (!isDrawing || !drawingState) return;
    set({ drawingState: { ...drawingState, currentX: x, currentY: y } });
  },

  finishDrawing: (shiftKey) => {
    const { activeTool, drawingState } = get();
    if (!drawingState) return;

    const { startX, startY, currentX, currentY } = drawingState;

    // Minimum size check
    const dx = Math.abs(currentX - startX);
    const dy = Math.abs(currentY - startY);
    if (dx < 1 && dy < 1) {
      set({ isDrawing: false, drawingState: null });
      return;
    }

    if (activeTool === 'wall') {
      let endX = currentX;
      let endY = currentY;
      if (shiftKey) {
        const snapped = snapAngle45(startX, startY, currentX, currentY);
        endX = snapped.x;
        endY = snapped.y;
      }
      const wall: FloorPlanWall = {
        id: crypto.randomUUID(),
        x1: startX, y1: startY,
        x2: endX, y2: endY,
        thickness: 0.8,
      };
      set((s) => ({ editWalls: [...s.editWalls, wall], isDrawing: false, drawingState: null }));
    } else if (activeTool === 'rect') {
      let w = currentX - startX;
      let h = currentY - startY;
      if (shiftKey) {
        const size = Math.max(Math.abs(w), Math.abs(h));
        w = w >= 0 ? size : -size;
        h = h >= 0 ? size : -size;
      }
      const x = w >= 0 ? startX : startX + w;
      const y = h >= 0 ? startY : startY + h;
      const rect: FloorPlanRect = {
        id: crypto.randomUUID(),
        x, y,
        width: Math.abs(w),
        height: Math.abs(h),
        rotation: 0,
      };
      set((s) => ({ editRects: [...s.editRects, rect], isDrawing: false, drawingState: null }));
    }
  },

  cancelDrawing: () => set({ isDrawing: false, drawingState: null }),

  addWall: (wall) => set((s) => ({ editWalls: [...s.editWalls, wall] })),
  addRect: (rect) => set((s) => ({ editRects: [...s.editRects, rect] })),

  updateWall: (id, updates) => set((s) => ({
    editWalls: s.editWalls.map((w) => w.id === id ? { ...w, ...updates } : w),
  })),

  updateRect: (id, updates) => set((s) => ({
    editRects: s.editRects.map((r) => r.id === id ? { ...r, ...updates } : r),
  })),

  deleteElement: (id) => {
    set((s) => ({
      editWalls: s.editWalls.filter((w) => w.id !== id),
      editRects: s.editRects.filter((r) => r.id !== id),
      selectedElement: s.selectedElement?.data.id === id ? null : s.selectedElement,
    }));
  },

  moveElement: (id, dx, dy) => {
    const { editWalls, editRects } = get();
    const wall = editWalls.find((w) => w.id === id);
    if (wall) {
      set((s) => ({
        editWalls: s.editWalls.map((w) =>
          w.id === id ? { ...w, x1: w.x1 + dx, y1: w.y1 + dy, x2: w.x2 + dx, y2: w.y2 + dy } : w
        ),
      }));
      return;
    }
    const rect = editRects.find((r) => r.id === id);
    if (rect) {
      set((s) => ({
        editRects: s.editRects.map((r) =>
          r.id === id ? { ...r, x: r.x + dx, y: r.y + dy } : r
        ),
      }));
    }
  },

  enterEditMode: () => {
    const { walls, rects } = get();
    set({
      editWalls: walls.map((w) => ({ ...w })),
      editRects: rects.map((r) => ({ ...r })),
      activeTool: 'select',
      selectedElement: null,
      isDrawing: false,
      drawingState: null,
    });
  },

  cancelEditMode: () => set({
    editWalls: [],
    editRects: [],
    activeTool: 'select',
    selectedElement: null,
    isDrawing: false,
    drawingState: null,
  }),

  loadFloorPlan: async (floorId) => {
    const generation = get().generation + 1;
    set({ generation });
    set({ isLoading: true });
    try {
      const data = await getFloorPlan(floorId);
      if (get().generation !== generation) return;
      set({ walls: data.walls, rects: data.rects });
    } finally {
      if (get().generation === generation) set({ isLoading: false });
    }
  },

  saveFloorPlan: async (floorId) => {
    const generation = get().generation;
    const { editWalls, editRects } = get();
    const data: FloorPlanData = { walls: editWalls, rects: editRects };
    await updateFloorPlan(floorId, data);
    if (get().generation !== generation) return;
    set({ walls: editWalls, rects: editRects });
  },
}));
